import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { Modal, Form, Row, Col, Button, Badge, Alert, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import LoadingOverlay from "app/orderTool/components/LoadingOverlay";
import SavRattachementSelect, { rattachementToPayload } from "app/orderTool/components/SavRattachementSelect";
import HelpTip from "app/orderTool/components/HelpTip";
import ClientFicheModal from "app/orderTool/components/ClientFicheModal";
import {
  getSav,
  getSavBareme,
  getSavCarte,
  updateSav,
  patchSavStatut,
  patchSavJalon,
  patchSavRattacher,
  createSav,
  addSavNote,
} from "state/sav/actions";
import {
  MOTIF_LABELS,
  SERVICE_LABELS,
  STATUT_LABELS,
  CANAL_LABELS,
  GESTE_LABELS,
  labelFor,
  euros,
} from "./savLabels";

const CHARGES = ["Julie", "Valentin", "Camille"];

const formatNoteDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch (e) {
    return d;
  }
};

const EMPTY_CREATE = {
  motif: "",
  service_concerne: "",
  description: "",
  charge_de_clientele: "",
  canal_declaration: "INTERNE",
  declare_par: "",
};

/**
 * Détail / traitement d'un SAV B2B : workflow, jalons, barème, carte d'excuse.
 */
const SavDetailModal = ({ show, savId, savCard, constants, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [sav, setSav] = useState(null);
  const [form, setForm] = useState({});
  const [bareme, setBareme] = useState(null);
  const [carte, setCarte] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [isCreate, setIsCreate] = useState(false);
  const [rattachementSelection, setRattachementSelection] = useState(null);
  const [ficheOpen, setFicheOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    if (!show) {
      setSav(null);
      setLoadError(null);
      setBareme(null);
      setCarte(null);
      setIsCreate(false);
      setNotes([]);
      setNoteText("");
      return;
    }
    if (savId) {
      setIsCreate(false);
      setRattachementSelection(null);
      loadSav(savId);
    } else {
      setIsCreate(true);
      setSav(null);
      setForm({ ...EMPTY_CREATE });
      setRattachementSelection(null);
    }
  }, [show, savId]);

  const loadSav = async (id) => {
    setLoading(true);
    setLoadError(null);
    const resp = await dispatch(getSav(id));
    const detail = resp?.sav || null;
    setSav(detail);
    setNotes(detail?.notes || []);
    if (!detail) {
      // Sans ça la modale restait vide et muette : l'utilisateur croyait la carte
      // « cassée » alors que seul le chargement avait échoué (retour Camille 02/07).
      setLoadError(resp?.message || "Impossible de charger ce SAV.");
    }
    if (detail) {
      setForm({
        service_concerne: detail.service_concerne || "",
        description: detail.description || "",
        commentaire_interne: detail.commentaire_interne || "",
        type_geste: detail.type_geste || "",
        montant_geste_centimes: detail.montant_geste_centimes ?? "",
        charge_de_clientele: detail.charge_de_clientele || "",
      });
    }
    setLoading(false);
  };

  const setField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleAddNote = async () => {
    const contenu = noteText.trim();
    if (!contenu || !sav?.id) {
      return;
    }
    setSavingNote(true);
    const resp = await dispatch(addSavNote(sav.id, contenu));
    setSavingNote(false);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Impossible d'ajouter le commentaire");
      return;
    }
    // Le commentaire rejoint le fil et le champ se vide pour le suivant.
    setNotes(resp?.sav?.notes || []);
    setNoteText("");
  };

  const handleSave = async () => {
    setSaving(true);
    if (isCreate) {
      if (!form.motif) {
        toast.error("Le motif est obligatoire");
        setSaving(false);
        return;
      }
      if (!rattachementSelection) {
        toast.error("Sélectionnez une commande groupée ou une prestation");
        setSaving(false);
        return;
      }
      const resp = await dispatch(createSav({
        ...form,
        ...rattachementToPayload(rattachementSelection),
      }));
      setSaving(false);
      if (resp?.error || resp?.success === false) {
        toast.error(resp?.message || "Création impossible");
        return;
      }
      toast.success(resp?.message || "SAV déclaré");
      onSaved?.();
      onClose();
      return;
    }
    if (!sav?.id) {
      // Le SAV n'a pas pu être chargé : sans ce garde-fou, `sav.id` levait une
      // TypeError, la promesse rejetait et le bouton restait bloqué sur
      // « Enregistrement… » indéfiniment (retour Camille 02/07).
      setSaving(false);
      toast.error("SAV non chargé : rien à enregistrer.");
      return;
    }
    // Le montant du geste n'est plus géré dans la fiche : on l'exclut du payload.
    const { montant_geste_centimes, ...payload } = form;
    let resp;
    try {
      resp = await dispatch(updateSav(sav.id, payload));
    } finally {
      setSaving(false);
    }
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Enregistrement impossible");
      return;
    }
    toast.success("SAV enregistré");
    setSav(resp?.sav || sav);
    onSaved?.();
  };

  const handleStatut = async (statut) => {
    if (statut === "REGLE") {
      if (!sav?.date_prise_en_charge || !sav?.date_solution_proposee) {
        toast.warning("Cochez les étapes « prise en charge » et « solution proposée » avant la clôture");
        return;
      }
    }
    setActionLoading(`statut-${statut}`);
    const resp = await dispatch(patchSavStatut(sav.id, statut));
    setActionLoading(null);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Changement de statut refusé");
      return;
    }
    toast.success(resp?.message || "Statut mis à jour");
    setSav(resp?.sav || sav);
    onSaved?.();
  };

  const handleJalon = async (jalon) => {
    setActionLoading(`jalon-${jalon}`);
    const resp = await dispatch(patchSavJalon(sav.id, jalon));
    setActionLoading(null);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Impossible de valider l'étape");
      return;
    }
    // Le message vient de l'API : il signale le passage automatique en « en cours »
    // quand le jalon est posé sur un SAV encore à traiter.
    toast.success(resp?.message || "Étape validée");
    setSav(resp?.sav || sav);
    onSaved?.();
  };

  const handleBareme = async () => {
    setActionLoading("bareme");
    const resp = await dispatch(getSavBareme(sav.id));
    setActionLoading(null);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Calcul barème impossible");
      return;
    }
    setBareme(resp);
    if (resp?.type_geste_suggere) {
      setField("type_geste", resp.type_geste_suggere);
    }
    if (resp?.montant_suggere_centimes) {
      setField("montant_geste_centimes", resp.montant_suggere_centimes);
    }
  };

  const handleCarte = async () => {
    setActionLoading("carte");
    const resp = await dispatch(getSavCarte(sav.id));
    setActionLoading(null);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Génération carte impossible");
      return;
    }
    setCarte(resp);
  };

  const copyCarte = () => {
    const texte = carte?.corps || carte?.texte;
    if (texte) {
      navigator.clipboard.writeText(texte);
      toast.success("Texte copié");
    }
  };

  const handleRattacher = async () => {
    if (!rattachementSelection) {
      toast.error("Sélectionnez une commande groupée ou une prestation");
      return;
    }
    setActionLoading("rattacher");
    const resp = await dispatch(patchSavRattacher(sav.id, rattachementToPayload(rattachementSelection)));
    setActionLoading(null);
    if (resp?.error || resp?.success === false) {
      toast.error(resp?.message || "Rattachement impossible");
      return;
    }
    toast.success(resp?.message || "SAV rattaché");
    setSav(resp?.sav || sav);
    setRattachementSelection(null);
    onSaved?.();
  };

  const transitions = constants?.transitions?.[sav?.statut] || [];
  const formDisabled = loading || saving || !!actionLoading;
  const displayRef = sav?.reference || savCard?.reference;

  return (
    <>
    <Modal show={show} onHide={onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          {isCreate ? "Déclarer un SAV" : displayRef || "SAV B2B"}
          {!isCreate && (sav?.statut || savCard?.statut) && (
            <Badge variant="info" className="ml-2">{labelFor(STATUT_LABELS, sav?.statut || savCard?.statut)}</Badge>
          )}
          {loading && (
            <Spinner animation="border" size="sm" variant="success" className="ml-2" />
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ position: "relative", minHeight: 160 }}>
        <LoadingOverlay show={loading} message="Chargement du SAV…" minHeight={160} />

        {loading && savCard && !isCreate && (
          <div className="text-muted small">
            <div><strong>{savCard.reference}</strong> — {labelFor(MOTIF_LABELS, savCard.motif)}</div>
            <div>{savCard.commande_groupee_numero || "Sans commande"}</div>
          </div>
        )}

        {!loading && isCreate && (
          <>
            <Form.Group>
              <Form.Label>Motif *</Form.Label>
              <Form.Control
                as="select"
                value={form.motif}
                onChange={(e) => setField("motif", e.target.value)}
              >
                <option value="">— Choisir —</option>
                {(constants?.motifs || []).map((m) => (
                  <option key={m} value={m}>{labelFor(MOTIF_LABELS, m)}</option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group>
              <Form.Label>Rattachement *</Form.Label>
              <SavRattachementSelect
                value={rattachementSelection}
                onChange={setRattachementSelection}
              />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Service concerné</Form.Label>
                  <Form.Control
                    as="select"
                    value={form.service_concerne}
                    onChange={(e) => setField("service_concerne", e.target.value)}
                  >
                    <option value="">—</option>
                    {(constants?.services || []).map((s) => (
                      <option key={s} value={s}>{labelFor(SERVICE_LABELS, s)}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Chargé de clientèle</Form.Label>
                  <Form.Control
                    as="select"
                    value={form.charge_de_clientele}
                    onChange={(e) => setField("charge_de_clientele", e.target.value)}
                  >
                    <option value="">—</option>
                    {CHARGES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
              />
            </Form.Group>
          </>
        )}

        {!loading && !isCreate && !sav && (
          <Alert variant="danger" className="mb-0">
            <div>{loadError || "Impossible de charger ce SAV."}</div>
            <Button variant="outline-danger" size="sm" className="mt-2" onClick={() => savId && loadSav(savId)}>
              Réessayer
            </Button>
          </Alert>
        )}

        {!loading && !isCreate && sav && (
          <fieldset disabled={formDisabled} style={{ border: "none", margin: 0, padding: 0, opacity: formDisabled && !loading ? 0.65 : 1 }}>
            <Row className="mb-2">
              <Col md={6}>
                <small className="text-muted">Motif</small>
                <div>{labelFor(MOTIF_LABELS, sav.motif)}</div>
              </Col>
              <Col md={6}>
                <small className="text-muted">Canal</small>
                <div>{labelFor(CANAL_LABELS, sav.canal_declaration)}</div>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col md={6}>
                <small className="text-muted">Client / Entreprise</small>
                <div>
                  {(sav.client_id || sav.entreprise_id) ? (
                    <Button variant="link" className="p-0 text-left" onClick={() => setFicheOpen(true)}>
                      {sav.client_nom || "—"}{sav.entreprise_nom ? ` — ${sav.entreprise_nom}` : ""}
                    </Button>
                  ) : (
                    <>{sav.client_nom || "—"}{sav.entreprise_nom ? ` — ${sav.entreprise_nom}` : ""}</>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <small className="text-muted">Commande / Prestation</small>
                <div>
                  {sav.prestation_label ? (
                    sav.commande_groupee_id ? (
                      <Button variant="link" className="p-0 text-left" onClick={() => { onClose(); history.push(`/commande?order_id=${sav.commande_groupee_id}&order_type=v2&from=sav`); }}>
                        <Badge variant="info" className="mr-1">Prestation</Badge>{sav.prestation_label}
                      </Button>
                    ) : (
                      <><Badge variant="info" className="mr-1">Prestation</Badge>{sav.prestation_label}</>
                    )
                  ) : sav.commande_groupee_numero ? (
                    <Button
                      variant="link"
                      className="p-0"
                      onClick={() => {
                        onClose();
                        history.push(`/commande?order_id=${sav.commande_groupee_id}&order_type=v2&from=sav`);
                      }}
                    >
                      {sav.commande_groupee_numero}
                    </Button>
                  ) : (
                    <Badge variant="warning">À rattacher</Badge>
                  )}
                </div>
              </Col>
            </Row>

            {sav.a_rattacher && (
              <Alert variant="warning">
                <div className="mb-2">
                  SAV importé ou non rattaché — liez une commande groupée ou une prestation.
                  <HelpTip title="Rattacher un SAV">
                    <p className="mb-0">Un SAV <strong>« à rattacher »</strong> vient en général d'un import d'historique : on connaît le client/le motif mais pas la commande exacte. Recherchez la commande (M/…) ou une prestation (P…) ci-dessous puis cliquez <strong>Rattacher</strong> pour le relier — les infos client/entreprise se complètent alors automatiquement.</p>
                  </HelpTip>
                </div>
                {sav.lien_devis_externe && (
                  <div className="small mb-2">
                    Lien source :{" "}
                    <a href={sav.lien_devis_externe} target="_blank" rel="noopener noreferrer">
                      {sav.lien_devis_externe}
                    </a>
                  </div>
                )}
                {(sav.entreprise_libelle || sav.client_libelle) && (
                  <div className="small mb-2 text-muted">
                    Libellés import : {sav.client_libelle || "—"} / {sav.entreprise_libelle || "—"}
                  </div>
                )}
                <SavRattachementSelect
                  value={rattachementSelection}
                  onChange={setRattachementSelection}
                  isDisabled={!!actionLoading}
                />
                <Button
                  size="sm"
                  variant="warning"
                  className="mt-2"
                  onClick={handleRattacher}
                  disabled={!!actionLoading}
                >
                  {actionLoading === "rattacher" ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    "Rattacher"
                  )}
                </Button>
              </Alert>
            )}

            <hr />
            <h6>
              Workflow
              <HelpTip title="Workflow du SAV">
                <p className="mb-1">Le SAV avance par étapes : <strong>À traiter → En cours → Réglé</strong>.</p>
                <p className="mb-1">Passer « En cours » <strong>bloque la facturation</strong> de la commande liée. Passer « Réglé » la débloque (si plus aucun SAV n'est ouvert sur cette commande).</p>
                <p className="mb-0">Seules les transitions autorisées sont proposées ; un SAV réglé peut être rouvert si besoin.</p>
              </HelpTip>
            </h6>
            <div className="d-flex flex-wrap mb-3">
              {transitions.map((t) => (
                <Button
                  key={t}
                  variant={t === "REGLE" ? "success" : "outline-primary"}
                  size="sm"
                  className="mr-2 mb-2"
                  onClick={() => handleStatut(t)}
                  disabled={!!actionLoading}
                >
                  {actionLoading === `statut-${t}` ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    `→ ${labelFor(STATUT_LABELS, t)}`
                  )}
                </Button>
              ))}
            </div>

            <h6>
              Étapes
              <HelpTip title="Les étapes de suivi">
                <p className="mb-1">Cochez les étapes clés au fur et à mesure du traitement :</p>
                <ul className="mb-1 pl-3">
                  <li><strong>Prise en charge</strong> : le moment où on commence à traiter le SAV.</li>
                  <li><strong>Solution proposée</strong> : le moment où on a proposé une réponse au client.</li>
                </ul>
                <p className="mb-0">Les deux étapes doivent être cochées avant de pouvoir clôturer le SAV (« Réglé »).</p>
              </HelpTip>
            </h6>
            <div className="mb-3">
              <Form.Check
                type="checkbox"
                id="etape-prise-en-charge"
                className="mb-1"
                label={
                  <span>
                    Prise en charge
                    {sav.date_prise_en_charge ? <small className="text-muted"> — {new Date(sav.date_prise_en_charge).toLocaleDateString("fr-FR")}</small> : ""}
                    {actionLoading === "jalon-prise_en_charge" && <Spinner animation="border" size="sm" className="ml-1" />}
                  </span>
                }
                checked={!!sav.date_prise_en_charge}
                disabled={!!sav.date_prise_en_charge || !!actionLoading}
                onChange={() => handleJalon("prise_en_charge")}
              />
              <Form.Check
                type="checkbox"
                id="etape-solution-proposee"
                className="mb-1"
                label={
                  <span>
                    Solution proposée
                    {sav.date_solution_proposee ? <small className="text-muted"> — {new Date(sav.date_solution_proposee).toLocaleDateString("fr-FR")}</small> : ""}
                    {actionLoading === "jalon-solution_proposee" && <Spinner animation="border" size="sm" className="ml-1" />}
                  </span>
                }
                checked={!!sav.date_solution_proposee}
                disabled={!!sav.date_solution_proposee || !!actionLoading}
                onChange={() => handleJalon("solution_proposee")}
              />
              {sav.date_cloture && (
                <Badge variant="dark">Clôturé le {new Date(sav.date_cloture).toLocaleDateString("fr-FR")}</Badge>
              )}
            </div>

            <hr />
            <h6>
              Geste commercial
              <HelpTip title="Geste commercial & barème">
                <p className="mb-1">Compensation éventuelle accordée au client (avoir, remise sur prochaine commande, réfaction…).</p>
                <p className="mb-0">Le bouton <strong>« Suggestion barème »</strong> propose un montant indicatif selon le motif (% du TTC de la commande, plafonné). C'est une aide à la décision : le chargé reste libre du montant final.</p>
              </HelpTip>
            </h6>
            <Row>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Type de geste</Form.Label>
                  <Form.Control
                    as="select"
                    value={form.type_geste}
                    onChange={(e) => setField("type_geste", e.target.value)}
                  >
                    <option value="">—</option>
                    {(constants?.gestes || []).map((g) => (
                      <option key={g} value={g}>{labelFor(GESTE_LABELS, g)}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={4} className="d-flex align-items-end">
                <Button variant="outline-info" onClick={handleBareme} className="mb-3" disabled={!!actionLoading}>
                  {actionLoading === "bareme" ? <Spinner animation="border" size="sm" /> : "Suggestion barème"}
                </Button>
              </Col>
            </Row>
            {bareme && (
              <Alert variant="info">
                Suggestion : {labelFor(GESTE_LABELS, bareme.type_geste_suggere)} — {euros(bareme.montant_suggere_centimes)}
                {" "}({bareme.taux_pourcent} % du TTC, plafond {euros(bareme.plafond_centimes)})
              </Alert>
            )}

            <Form.Group>
              <Form.Label>Commentaire interne</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.commentaire_interne}
                onChange={(e) => setField("commentaire_interne", e.target.value)}
              />
            </Form.Group>

            {/* Fil de commentaires : le commentaire rédigé s'intègre à la carte et
                libère le champ pour le suivant, comme sur les cartes événement du
                pipeline (retour Camille 17/09). */}
            <hr />
            <h6>Commentaires</h6>
            <Form.Group>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Ajouter un commentaire (échange client, décision, suite donnée…)"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                disabled={savingNote}
              />
              <Button
                variant="outline-primary"
                size="sm"
                className="mt-1"
                onClick={handleAddNote}
                disabled={savingNote || !noteText.trim()}
              >
                {savingNote ? <Spinner animation="border" size="sm" /> : "Ajouter le commentaire"}
              </Button>
            </Form.Group>
            {notes.length > 0 && (
              <div style={{ maxHeight: 220, overflowY: "auto" }}>
                {notes.map((note) => (
                  <div key={note.id} className="mb-2 pb-2" style={{ borderBottom: "1px solid #eee" }}>
                    <div style={{ fontSize: "0.75rem", color: "#888" }}>
                      {formatNoteDate(note.created_at)}
                      {note.auteur ? ` — ${note.auteur}` : ""}
                    </div>
                    <div style={{ whiteSpace: "pre-wrap" }}>{note.contenu}</div>
                  </div>
                ))}
              </div>
            )}

            <hr />
            <h6>
              Carte d'excuse
              <HelpTip title="Carte d'excuse">
                <p className="mb-0">Génère un <strong>brouillon</strong> de mot d'excuse personnalisé (client, motif, commande). Rien n'est envoyé : le texte est à relire puis copier pour l'utiliser (carte jointe à la prochaine prestation, email…).</p>
              </HelpTip>
            </h6>
            <Button variant="outline-secondary" size="sm" onClick={handleCarte} className="mr-2" disabled={!!actionLoading}>
              {actionLoading === "carte" ? <Spinner animation="border" size="sm" /> : "Générer le brouillon"}
            </Button>
            {carte?.corps && (
              <>
                <Button variant="link" size="sm" onClick={copyCarte}>Copier</Button>
                <pre style={{ background: "#f5f5f5", padding: "12px", whiteSpace: "pre-wrap", fontSize: "0.9rem" }}>
                  {carte.corps}
                </pre>
              </>
            )}
          </fieldset>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving || !!actionLoading}>Fermer</Button>
        <Button variant="success" onClick={handleSave} disabled={loading || saving || !!actionLoading}>
          {saving ? <><Spinner animation="border" size="sm" className="mr-1" /> Enregistrement…</> : (isCreate ? "Déclarer" : "Enregistrer")}
        </Button>
      </Modal.Footer>
    </Modal>
    <ClientFicheModal
      show={ficheOpen}
      clientId={sav?.client_id}
      entrepriseId={sav?.entreprise_id}
      onClose={() => setFicheOpen(false)}
    />
    </>
  );
};

export default SavDetailModal;
